<template>
  <div class="border rounded-lg p-5 h-fit min-w-52 max-w-100">
    <SocialTypeInfo :social-type="postData.type" :size="SocialIconSize.LG" />
    <div
      class="flex flex-col"
      :class="{
        'flex-col-reverse mt-5': postData.type === ContentSocialType.INSTAGRAM,
      }"
    >
      <SocialPostDescription
        :description="postData.description"
        :is-customizable="props.isCustomizable"
        @update:error-state="postData.descriptionErrorState = $event"
      />
      <SelectableCarousel
        v-if="postData.socialFiles.length > 1"
        ref="quadrantsOverlay"
        :variant="socialTypeCarousel[postData.type]"
        :files="carouselFiles"
        :selected-index="selectedFileIndex"
        :customizable="props.isCustomizable || false"
        :quadrants="props.quadrants || []"
        :original-dimensions="props.originalDimensions"
        :logo="postData.logo"
        :logos="postData.logos"
        :preview="false"
        class="flex-grow"
        @load="rerenderStage"
        @update:selected-index="onSelectedFileIndexUpdate"
        @update:image-data="updateImage"
        @update:logo="updateLogo"
      />

      <div v-else-if="isFileImage(singleFileUrl)" class="m-auto mt-5 relative">
        <ClientOnly v-if="showQuadrantsOverlay">
          <QuadrantsOverlay
            ref="quadrantsOverlay"
            :quadrants="props.quadrants || []"
            :original-dimensions="props.originalDimensions"
            img-id="preview-image"
            :logo="postData.logo"
            @update:image-data="updateImage"
            @update:logo="updateLogo"
          />
        </ClientOnly>
        <img
          id="preview-image"
          :src="singleFileUrl"
          class="rounded-md object-contain max-h-100 w-full h-full"
          alt="Image File"
          @load="rerenderStage"
        />
      </div>
      <div
        v-else-if="isFileVideo(singleFileUrl)"
        class="w-full rounded-md aspect-video m-auto mt-5"
      >
        <video class="w-full h-full rounded-md post-video" controls>
          <source :src="`${singleFileUrl}#t=0.001`" />
        </video>
      </div>
      <div
        v-else
        class="min-h-80 max-h-91 m-auto aspect-square mt-5 flex items-center justify-center bg-disabled text-surface rounded-md"
      >
        <ImagePlaceholder />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ImagePlaceholder from '~/assets/images/placeholders/image-placeholder.svg?component';
import {
  SocialTypeInfo,
  SocialIconSize,
} from '~/pages/_components/social-type-info';
import { Nullable } from '~/types/common';
import { LogoDto } from '~/composables/use-quadrants';
import { Carousel, SelectableCarousel } from '~/components/carousel';
import { socialTypeCarousel } from '~/constants/social-posts';
import { PostModalContentForm } from '~/pages/content/my-posts/_components/post-modal-content/post-modal-content.types';
import { SocialPostDescription } from '~/pages/content/my-posts/_components/social-post-preview/components/social-post-description';
import { ContentSocialType } from '~/api/contents';

const QuadrantsOverlay = defineAsyncComponent(
  () =>
    import('~/pages/content/my-posts/_components/quadrants-overlay/quadrants-overlay.vue'),
);

type Props = {
  isEditable?: boolean;
  isCustomizable?: boolean;
  quadrants?: Box[];
  originalDimensions?:
    | { width: number; height: number; type: string }
    | Record<number, { width: number; height: number; type: string }>
    | null;
  selectedFileIndex?: number;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:selected-file-index': [index: number];
}>();

const quadrantsOverlay = ref<Nullable<any>>(null);
const selectedFileIndex = ref(props.selectedFileIndex || 0);

const showQuadrantsOverlay = computed(
  () => (props.isCustomizable || props.isEditable) && props.quadrants?.length,
);

const postData = defineModel<PostModalContentForm>('postData', {
  required: true,
});

const singleFileUrl = computed(() => postData.value.socialFiles[0]?.file?.url);

const rerenderStage = () => {
  if (quadrantsOverlay.value) {
    quadrantsOverlay.value.rerenderStage('preview-image');
  }
};

const onSelectedFileIndexUpdate = (index: number) => {
  selectedFileIndex.value = index;
  emit('update:selected-file-index', index);
};

const updateImage = (imageData: string, pageIndex?: number) => {
  if (pageIndex === undefined || postData.value.socialFiles.length === 1) {
    postData.value.imageData = imageData;
    return;
  }

  if (!postData.value.renderedImages) {
    postData.value.renderedImages = [];
  }

  const filtered = postData.value.renderedImages.filter(
    (img) => img.pageIndex !== pageIndex,
  );

  filtered.push({
    pageIndex,
    dataUrl: imageData,
  });

  postData.value.renderedImages = filtered;

  const uniquePages = new Set(
    postData.value.renderedImages.map((img) => img.pageIndex),
  );
};
const updateLogo = (logo: Partial<LogoDto>) => {
  postData.value.logo = {
    ...(postData.value.logo as LogoDto),
    ...logo,
  };
};

const carouselFiles = computed(() =>
  postData.value.socialFiles.map(({ file }) => ({
    id: file.id,
    src: file.url,
  })),
);

const getAllLogos = (accountLogoId: number | string) => {
  if (quadrantsOverlay.value && quadrantsOverlay.value.getAllLogos) {
    return quadrantsOverlay.value.getAllLogos(accountLogoId);
  }
  return [];
};

const renderAllPages = async () => {
  if (postData.value.socialFiles.length <= 1) {
    return;
  }

  const originalIndex = selectedFileIndex.value;
  const totalPages = postData.value.socialFiles.length;

  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < totalPages; i++) {
    selectedFileIndex.value = i;
    emit('update:selected-file-index', i);

    await nextTick();

    await new Promise((resolve) => {
      setTimeout(() => resolve(), 400);
    });

    if (quadrantsOverlay.value) {
      quadrantsOverlay.value.rerenderStage('customization-preview-image');
    }

    if (
      quadrantsOverlay.value &&
      quadrantsOverlay.value.saveAllLogosForCurrentPage
    ) {
      quadrantsOverlay.value.saveAllLogosForCurrentPage();
    }

    await new Promise((resolve) => {
      setTimeout(() => resolve(), 200);
    });
  }
  /* eslint-enable no-await-in-loop */

  selectedFileIndex.value = originalIndex;
  emit('update:selected-file-index', originalIndex);
  await nextTick();
};

defineExpose({
  getAllLogos,
  renderAllPages,
});
</script>
